import {HttpContext} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {plainToClass, plainToClassFromExist} from 'class-transformer';
import {environment} from '../../../environments/environment';
import {StaticPageApiModel} from '../models/static-page.api.model';
import {ResultListApiModel} from '../models/result-list.api.model';
import {QueryParamsApiModel} from '../models/query-params-api.model';
import {QueryParamsService} from '../../core/services/query-params.service';

@Injectable({
  providedIn: 'root'
})
export class StaticPageRestService {
  private readonly resource = '/static_pages';
  private readonly adminNoCacheHeaders = new HttpHeaders({'x-cache-bypass': 'true'});

  constructor(
    private readonly http: HttpClient,
    private readonly params: QueryParamsService
  ) {
  }

  public getBySlug(slug: string, options?: {bypassCache?: boolean; context?: HttpContext}): Observable<StaticPageApiModel> {
    return this.http.get<StaticPageApiModel>(`${environment.apiUrl}${this.resource}/${slug}`, {
      context: options?.context,
      headers: options?.bypassCache ? this.adminNoCacheHeaders : undefined
    })
      .pipe(map((result: StaticPageApiModel) => plainToClass(StaticPageApiModel, result)));
  }

  public getByPublicSlug(publicSlug: string, options?: {bypassCache?: boolean; context?: HttpContext}): Observable<StaticPageApiModel> {
    return this.http.get<StaticPageApiModel>(`${environment.apiUrl}/pages/resolve/${encodeURIComponent(publicSlug)}`, {
      context: options?.context,
      headers: options?.bypassCache ? this.adminNoCacheHeaders : undefined
    }).pipe(map((result: StaticPageApiModel) => plainToClass(StaticPageApiModel, result)));
  }

  public getByPath(pathOrUrl: string, locale?: string, options?: {bypassCache?: boolean}): Observable<StaticPageApiModel> {
    let headers = new HttpHeaders();
    if (locale) {
      headers = headers.set('Accept-Language', locale);
    }
    if (options?.bypassCache) {
      headers = headers.set('x-cache-bypass', 'true');
    }

    return this.http.get<StaticPageApiModel>(this.buildApiUrl(pathOrUrl), {
      headers: headers.keys().length ? headers : undefined
    })
      .pipe(map((result: StaticPageApiModel) => plainToClass(StaticPageApiModel, result)));
  }

  public getCollection(
    queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel(),
    options?: {bypassCache?: boolean}
  ): Observable<ResultListApiModel<StaticPageApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);

    return this.http.get<ResultListApiModel<StaticPageApiModel>>(`${environment.apiUrl}${this.resource}`, {
      params,
      headers: options?.bypassCache ? this.adminNoCacheHeaders : undefined
    })
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<StaticPageApiModel>(StaticPageApiModel), result as object)));
  }

  public getNavigation(placement: 'landing_footer' | 'app_menu'): Observable<StaticPageApiModel[]> {
    return this.http.get<any>(`${environment.apiUrl}/pages/navigation`, {
      headers: this.adminNoCacheHeaders,
      params: {
        placement
      }
    }).pipe(map((result: any) => {
      const items = Array.isArray(result) ? result : (result?.['hydra:member'] || []);

      return items.map((item: StaticPageApiModel) => plainToClass(StaticPageApiModel, item));
    }));
  }

  public create(payload: Partial<StaticPageApiModel>): Observable<StaticPageApiModel> {
    return this.http.post<StaticPageApiModel>(`${environment.apiUrl}${this.resource}`, payload)
      .pipe(map((result: StaticPageApiModel) => plainToClass(StaticPageApiModel, result)));
  }

  public update(page: StaticPageApiModel, payload: Partial<StaticPageApiModel>): Observable<StaticPageApiModel> {
    return this.http.patch<StaticPageApiModel>(`${environment.apiUrl}${page['@id']}`, payload, {
      headers: new HttpHeaders({
        'Content-Type': 'application/merge-patch+json',
      })
    })
      .pipe(map((result: StaticPageApiModel) => plainToClass(StaticPageApiModel, result)));
  }

  public patchByPath(pathOrUrl: string, payload: Record<string, any>, locale: string): Observable<StaticPageApiModel> {
    return this.http.patch<StaticPageApiModel>(this.buildApiUrl(pathOrUrl), payload, {
      headers: new HttpHeaders({
        'Accept-Language': locale,
        'Content-Type': 'application/merge-patch+json',
      })
    }).pipe(map((result: StaticPageApiModel) => plainToClass(StaticPageApiModel, result)));
  }

  public delete(page: StaticPageApiModel): Observable<unknown> {
    return this.http.delete(`${environment.apiUrl}${page['@id']}`);
  }

  public buildPublicRoute(page: Pick<StaticPageApiModel, 'slug' | 'publicSlug'>): string {
    return `/pages/${page.publicSlug || page.slug}`;
  }

  private buildApiUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) {
      return pathOrUrl;
    }

    const base = environment.apiUrl.replace(/\/+$/, '');
    const path = pathOrUrl.replace(/^\/+/, '');

    if (/\/api$/i.test(base) && /^api\//i.test(path)) {
      return `${base}/${path.replace(/^api\//i, '')}`;
    }

    return `${base}/${path}`;
  }
}
